import { useCallback } from "react";

import { format } from "date-fns";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";

import { useAppSelector } from "app/hooks";
import { selectCurrentPractice } from "features/auth/auth-slice";
import { useValidateRoomAvailabilityMutation } from "services/appointments";
import { Clinic } from "services/types/clinics.type";
import { Patient } from "services/types/patients.type";
import { Room } from "services/types/rooms.type";

export default function useRoomValidation() {
  const [validateRoomAvailability] = useValidateRoomAvailabilityMutation();
  const currentPractice = useAppSelector(selectCurrentPractice);

  const isRoomAvailable = useCallback(
    async (
      room: Room,
      appointmentDate: Date,
      startTime: Date,
      endTime: Date,
      title: string,
      patient: Patient,
      clinic: Clinic,
      appointmentId?: number
    ) => {
      if (currentPractice?.scheduling_ignore_room_reservation) {
        return true;
      }
      const startMoment = new Date(appointmentDate);
      startMoment.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

      const endMoment = new Date(appointmentDate);
      endMoment.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);
      try {
        const result = await validateRoomAvailability({
          room: room.id.toString(),
          start_moment: formatInTimeZone(
            format(
              clinic?.timezone
                ? zonedTimeToUtc(startMoment, clinic.timezone)
                : startMoment,
              "yyyy-MM-dd HH:mm:ssXXX"
            ),
            clinic?.timezone || "UTC",
            "yyyy-MM-dd HH:mm:ssXXX"
          ),
          end_moment: formatInTimeZone(
            format(
              clinic?.timezone
                ? zonedTimeToUtc(endMoment, clinic.timezone)
                : endMoment,
              "yyyy-MM-dd HH:mm:ssXXX"
            ),
            clinic?.timezone || "UTC",
            "yyyy-MM-dd HH:mm:ssXXX"
          ),
          validation_mode: "room",
          title: title || "Appointment",
          patient: patient?.id,
          clinic: clinic.id,
          ...(appointmentId && { current_appointment_id: appointmentId })
        });

        if ("error" in result) {
          return false;
        }
        return result.data.isValid;
      } catch {
        return false;
      }
    },
    [
      validateRoomAvailability,
      currentPractice?.scheduling_ignore_room_reservation
    ]
  );

  return {
    isRoomAvailable
  };
}
